import moment from 'moment';
import {
  CSSProperties,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import { Button } from '@shared/components/Button';
import { Icon, IconName } from '@shared/components/Icon';
import { Text, TextAlign } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBTextInput } from '@shared/components/textInput';
import { ColorThemeContext } from '@shared/styles';
import styles from './ComboDatePickerField.module.css';

export interface FocusRef {
  focus(): void;
}

interface Props {
  label: string;
  icon?: IconName;
  value: string;
  style?: CSSProperties;
  textAlign?: TextAlign;
  allowCustomInput?: boolean;
  formats: string[];
  highlight?: boolean;
  onChange(value: string | undefined): void;
  onBlur?(): void;
  onFocus?(): void;
  autoFocus?: boolean;
  forwardRef?: MutableRefObject<FocusRef | null>;
}

export function MBComboDatePickerField({
  label,
  icon,
  formats,
  value: _value,
  style,
  highlight,
  textAlign,
  allowCustomInput = true,
  onChange,
  onBlur,
  onFocus,
  autoFocus,
  forwardRef,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [value, setValue] = useState(_value || '');
  useEffect(() => setValue(_value || ''), [_value]);

  const [active, setActive] = useState(false);
  const showTextInput = active && allowCustomInput;
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const textInputRef = useRef<MBTextInput>(null);
  const focusTimeoutRef = useRef<Timeout | null>(null);

  const focus = useCallback(() => {
    setActive(true);
    onFocus?.();
    // Android Hack: Focus must happen some time after the state changes to work correctly
    focusTimeoutRef.current = setTimeout(() => {
      textInputRef.current?.focus();
      focusTimeoutRef.current = null;
    }, 100);
  }, [onFocus]);

  if (forwardRef) {
    // Expose the real focus method so parents can programmatically focus this field
    forwardRef.current = { focus };
  }

  useEffect(() => {
    if (autoFocus) {
      focus();
    }
  }, [autoFocus, focus]);

  const clear = useCallback(() => {
    // if the input is empty, clicking the clear button has the same effect as blurring
    if (value === '') {
      setActive(false);
      onBlur?.();
    } else {
      onChange(undefined);
      textInputRef.current?.focus();
    }
  }, [onChange, value, onBlur]);

  const blur = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      // Cancel any pending delayed focus so the input doesn't steal focus again while scrolling
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
      if ((event?.nativeEvent as any)?.relatedTarget === clearButtonRef.current) {
        // this blur event originated from clicking the clear button
        // in this specific scenario, we do not want to deactivate but allow the clear callback to run
        return;
      }

      const parsed = moment(value, formats);
      if (value !== _value && parsed.isValid()) {
        onChange(parsed.toISOString());
      } else {
        setValue(_value);
      }

      setActive(false);
      onBlur?.();
    },
    [onBlur, value, _value, formats, onChange],
  );

  const handleChange = useCallback(
    (text: string) => {
      setValue(text);
      // strict validation when typing
      const parsed = moment(text, formats, true);
      if (text !== _value && parsed.isValid()) {
        onChange(parsed.toISOString());
      }
    },
    [formats, _value, onChange],
  );

  return (
    <View
      className={[styles.field, (showTextInput || highlight) && styles.active]}
      style={style}
      onClick={showTextInput ? undefined : focus}
    >
      {!!icon && (
        <Icon
          name={icon}
          gridSize={4}
          color={active || Boolean(value) ? colors.accent : colors.input.label}
        />
      )}
      <View paddingRight={0.5} />
      {showTextInput && (
        <MBTextInput
          ref={textInputRef}
          value={value}
          onChangeText={handleChange}
          noPadding
          noBorder
          selectTextOnFocus
          textContentType="none"
          autoCorrect={false}
          placeholder={label}
          onBlur={blur}
          keyboardType={'numbers-and-punctuation' as any}
          align={!active && Boolean(value) ? 'right' : 'left'}
          style={{ flex: 1, minWidth: 0 }}
        />
      )}
      {!showTextInput && (
        <View flex>
          {!!value && (
            <Text size="body1" align={textAlign} numberOfLines={1}>
              {value}
            </Text>
          )}
          {!value && (
            <Text size="body1" weight="semibold" align={textAlign}>
              {label}
            </Text>
          )}
        </View>
      )}

      {(showTextInput || (highlight && !!value)) && (
        <Button
          ref={clearButtonRef}
          variant="ghost"
          size="small"
          iconOnly
          icon="exit-heavy"
          iconGridSize={2.5}
          vars={{ 'button-foreground': colors.foregroundInactive }}
          // keep focus on the text input so clicking clear doesn't blur/deactivate the field
          onPointerDown={event => event.preventDefault()}
          onClick={clear}
        />
      )}
    </View>
  );
}
